import { useCallback, useEffect, useState } from 'react';
import {
  checkGoodreadsSession,
  clearGoodreadsSession,
  fetchGoodreadsSession,
  setGoodreadsSession,
} from '../api.js';
import { relativeTime } from '../../../shared/time.js';
import { useToast } from '../Toaster.jsx';

// The Goodreads session: every bookshelf in the service is read through it,
// signed in as Litbyt's own Goodreads account, since Goodreads retired the
// feed that needed no sign-in. When Goodreads lets it lapse every shelf stops
// updating at once, and this page is where that is fixed.
//
// The cookie is write-only. The API never sends it back, so this page shows
// whose it is and when it was set, never the value.
export default function GoodreadsPanel() {
  const notify = useToast();
  const [session, setSession] = useState(null);
  const [cookie, setCookie] = useState('');
  const [busy, setBusy] = useState('');
  const [confirming, setConfirming] = useState(false);
  // What the last check said, so "working" means "working when asked", with
  // the time it was asked, rather than an indefinite claim.
  const [check, setCheck] = useState(null);

  const reload = useCallback(() => {
    fetchGoodreadsSession()
      .then(setSession)
      .catch((error) => notify(`Could not load the Goodreads session: ${error.message}`, { tone: 'error' }));
  }, []);

  useEffect(reload, [reload]);

  async function handleSave(event) {
    event.preventDefault();
    setBusy('save');
    try {
      const saved = await setGoodreadsSession(cookie);
      setSession(saved);
      setCookie('');
      setCheck({ ok: true, at: new Date().toISOString() });
      notify(`Signed in to Goodreads as user ${saved.goodreadsUserId}.`);
    } catch (error) {
      notify(describe(error), { tone: 'error' });
    } finally {
      setBusy('');
    }
  }

  async function handleCheck() {
    setBusy('check');
    try {
      await checkGoodreadsSession();
      setCheck({ ok: true, at: new Date().toISOString() });
    } catch (error) {
      setCheck({ ok: false, at: new Date().toISOString(), message: describe(error) });
    } finally {
      setBusy('');
      // A check lifts the pause when it passes and starts one when it does
      // not, so what the page says about it is read again either way.
      reload();
    }
  }

  async function handleClear() {
    setBusy('clear');
    try {
      setSession(await clearGoodreadsSession());
      setCheck(null);
      notify('Goodreads session removed. Shelves stop updating until a new one is set.');
    } catch (error) {
      notify(`Could not remove the session: ${error.message}`, { tone: 'error' });
    } finally {
      setBusy('');
      setConfirming(false);
    }
  }

  const configured = Boolean(session?.configured);
  const pausedUntil = session?.pausedUntil;

  return (
    <>
      <section className="panel goodreads-session">
        <div className="panel-header">
          <h2>Goodreads</h2>
          {session && (
            <span className={`connection-status ${statusTone(configured, check, pausedUntil)}`}>
              {statusLabel(configured, check, pausedUntil)}
            </span>
          )}
        </div>
        <p className="panel-hint">
          Every bookshelf is read from Goodreads signed in as Litbyt's own Goodreads account,
          with that account's session cookie. When Goodreads ends the session, every shelf stops
          updating at once and keeps showing what it last had, until a new cookie is set here.
        </p>

        {pausedUntil && (
          <p className="account-status goodreads-paused">
            Goodreads turned this session away, so no shelf is using it. One sync tries it again{' '}
            {new Date(pausedUntil) > new Date() ? relativeTime(pausedUntil) : 'as soon as one is due'};
            setting a new cookie below, or a check that passes, puts every shelf back to work at once.
          </p>
        )}

        {configured ? (
          <p className="account-status">
            Signed in as Goodreads user{' '}
            <a
              href={`https://www.goodreads.com/user/show/${session.goodreadsUserId}`}
              target="_blank"
              rel="noreferrer"
            >
              {session.goodreadsUserId}
            </a>
            , set {relativeTime(session.updatedAt)}
            {session.updatedBy ? ` by ${session.updatedBy}` : ''}.
            {check && (
              <>
                {' '}
                {check.ok ? `Working when checked ${relativeTime(check.at)}.` : check.message}
              </>
            )}
          </p>
        ) : (
          session && <p className="account-status">No session is set. No bookshelf can sync until one is.</p>
        )}

        {configured && (
          <div className="panel-actions goodreads-actions">
            <button type="button" onClick={handleCheck} disabled={busy !== ''}>
              {busy === 'check' ? 'Checking…' : 'Check now'}
            </button>
            {confirming ? (
              <>
                <button type="button" className="danger" onClick={handleClear} disabled={busy !== ''}>
                  {busy === 'clear' ? 'Removing…' : 'Confirm remove'}
                </button>
                <button type="button" onClick={() => setConfirming(false)}>
                  Cancel
                </button>
              </>
            ) : (
              <button type="button" className="danger" onClick={() => setConfirming(true)}>
                Remove
              </button>
            )}
          </div>
        )}
      </section>

      <form className="panel" onSubmit={handleSave}>
        <h2>{configured ? 'Replace the session' : 'Set the session'}</h2>
        <ol className="panel-hint goodreads-steps">
          <li>Sign in to goodreads.com as Litbyt's Goodreads account - not your own.</li>
          <li>Open DevTools, choose the Network tab, and reload the page.</li>
          <li>Click any request to goodreads.com, and copy the value of its Cookie request header.</li>
        </ol>
        <label className="field">
          <span>Cookie header</span>
          <textarea
            className="cookie-input"
            rows={4}
            value={cookie}
            spellCheck={false}
            autoComplete="off"
            placeholder="ccsid=…; session-token=…; …"
            onChange={(event) => setCookie(event.target.value)}
          />
          <span className="field-hint">
            Goodreads is asked whether it works before it is saved, so a cookie that does not
            sign in never replaces one that does.
          </span>
        </label>
        <button type="submit" className="save-button" disabled={busy !== '' || cookie.trim() === ''}>
          {busy === 'save' ? 'Checking with Goodreads…' : 'Check and save'}
        </button>
      </form>
    </>
  );
}

function statusTone(configured, check, pausedUntil) {
  if (!configured || pausedUntil || (check && !check.ok)) return 'error';
  return check?.ok ? 'active' : '';
}

function statusLabel(configured, check, pausedUntil) {
  if (!configured) return 'Not set';
  if (pausedUntil) return 'Paused';
  if (!check) return 'Set';
  return check.ok ? 'Working' : 'Not working';
}

function describe(error) {
  if (error.status === 422) {
    return 'Goodreads did not accept that cookie. Sign in again and copy a fresh one.';
  }
  if (error.status === 502) {
    return `Could not reach Goodreads to check: ${error.message}`;
  }
  return error.message;
}
